import { ScaleBand, ScaleLinear, scaleBand, scaleLinear } from "d3";
import { EEGDisplayConfig } from "../../../../Types/EEG";
import { MarginedBoundingBox } from "../../../../Types/MarginedBoundingBox";
import { SdDetectionReactCallbacks } from "../../../../Types/ReactCallbacks";
import { SdDetectionConfig } from "../../../../Types/SdDetection";
import { D3TimeBasedVisualization } from "../../D3TimeBasedVisualization";
import { D3SdDetectionConfigurationBuilder } from "./D3SdDetectionConfigurationBuilder";
import { D3SdDetectionRenderer } from "./D3SdDetectionRenderer";
import { Offset } from "../../../../Types/Offset";
import { EEGPage } from "../../../../Data/EEGPage";
import { TraceDataConfig } from "../../../../Types/Trace";
import { LEFT_MARGIN, RIGHT_MARGIN } from "../../Constants";
import { ModalityDataSource } from "../../../../Types/ModalityDataSource";
import { DataSource } from "../../../../Types/DataSource";
import { SDTimeSeriesPageManager } from "../../../../Data/SDTimeSeriesPageManager";
import { ModalityPage } from "../../../../Data/ModalityPage";
import { DEFAULT_PX_PER_MM } from "../../../../Calibration/constants";

export class D3SdDetection extends D3TimeBasedVisualization<SdDetectionConfig, SdDetectionReactCallbacks, D3SdDetectionRenderer, SDTimeSeriesPageManager> {
    public graphBoundingBox: MarginedBoundingBox
    public margins: Offset = { top: 30, left: LEFT_MARGIN, bottom: 90, right: RIGHT_MARGIN }
    public mainEEGConfig: EEGDisplayConfig
    public overlayEEGConfig: EEGDisplayConfig
    public channelScale: ScaleBand<string> = scaleBand()
	public overlayBoundingBox: MarginedBoundingBox
	public overlayMargins = { top: 0, left: 0, bottom: 0, right: 0 }
	public mainEEGSensitivityScale: ScaleLinear<any, any, any> = scaleLinear()
    public overlayEEGSensitivityScale: ScaleLinear<any, any, any> = scaleLinear()

    constructor(root: HTMLDivElement, config: SdDetectionConfig, pageManager: SDTimeSeriesPageManager, reactCallbacks: SdDetectionReactCallbacks) {
        super(root, config, pageManager, reactCallbacks)
        this.graphBoundingBox = new MarginedBoundingBox(config.dimensions, this.margins)
        this.overlayBoundingBox = new MarginedBoundingBox(config.dimensions, this.overlayMargins)
        this.mainEEGConfig = new EEGDisplayConfig(config.montage, config.eegConfig.LFF, config.eegConfig.HFF, config.eegConfig.notch, config.eegConfig.sensitivityMicroVolts, config.montageMapping)
        this.overlayEEGConfig = new EEGDisplayConfig(config.montage, config.overlayEEGConfig.LFF, config.overlayEEGConfig.HFF, config.overlayEEGConfig.notch, config.overlayEEGConfig.sensitivityMicroVolts, config.montageMapping)

        this.mount(new D3SdDetectionRenderer(this, new D3SdDetectionConfigurationBuilder(this), "d3-sd-detection"))
    }

    getLastHoveredDate = () => this.renderer?.getLastHoveredDate()

	getOverlay = () => this.renderer?.overlay

    public getVisibleTraces(): TraceDataConfig[] {
        const visibleLeads = new Set(
            this.mainEEGConfig.channelDisplayInfo
                .filter(({ key }) => !this.config.hiddenChannelKeys.has(key))
                .flatMap(({ prefixedLeads }) => prefixedLeads)
        )

        return this.mainEEGConfig.leads
            .map((lead, index) => ({ 
                id: lead,
                name: lead,
                dataKey: lead,
                rawDataKey: lead,
                dataSource: DataSource.CURRENT_PATIENT,
                units: "uV", 
                isCompositePart: true, 
                compositeIndex: index,
            }))
            .filter(trace => visibleLeads.has(trace.dataKey))
    }

	protected renderPage(page: EEGPage): void {
		this.renderer?.eegCanvases?.renderPage(page)
	}

	protected getModalityDataSources(): ModalityDataSource[] {
        const modalities = [this.mainEEGConfig?.eegModality, this.overlayEEGConfig?.eegModality]

        return modalities
            .filter((modality): modality is string => modality !== undefined)
            .map((modality: string) => ({
                modality,
                dataObjectId: this.reactCallbacks.dataSourceMap.get(DataSource.CURRENT_PATIENT) as number,
            }) 
        )
	}
    
    protected updateDerivedState = () => {
        this.graphBoundingBox.setDimensions(this.config.dimensions)
		this.overlayBoundingBox.setDimensions({width: this.graphBoundingBox.width, height: this.graphBoundingBox.height})
        this.config.viewScale.range([0, this.graphBoundingBox.width])
        this.mainEEGConfig = new EEGDisplayConfig(this.config.montage, this.config.eegConfig.LFF, this.config.eegConfig.HFF, this.config.eegConfig.notch, this.config.eegConfig.sensitivityMicroVolts, this.config.montageMapping)
        this.overlayEEGConfig = new EEGDisplayConfig(this.config.montage, this.config.overlayEEGConfig.LFF, this.config.overlayEEGConfig.HFF, this.config.overlayEEGConfig.notch, this.config.overlayEEGConfig.sensitivityMicroVolts, this.config.montageMapping) 
        
        this.channelScale
            .domain(this.mainEEGConfig.channelDataKeys)
            .range([0, this.graphBoundingBox.height])

        this.mainEEGSensitivityScale
          .domain([-this.config.eegConfig.sensitivityMicroVolts * 1e6, this.config.eegConfig.sensitivityMicroVolts * 1e6])
          .range([-DEFAULT_PX_PER_MM, DEFAULT_PX_PER_MM])

        this.overlayEEGSensitivityScale
            .domain([-this.config.overlayEEGConfig.sensitivityMicroVolts * 1e6, this.config.overlayEEGConfig.sensitivityMicroVolts * 1e6])
            .range([-DEFAULT_PX_PER_MM, DEFAULT_PX_PER_MM])

		this.renderer?.updateChildren()
    }

    protected updatePageManager(): void {
        const pageManagerConfig = this.getPageManagerConfig()

        this.timeSeriesPageManager.update(pageManagerConfig, this.onPageLoaded, this.onPageUnloaded, this.onPageLoadStart)
        this.timeSeriesPageManager.updateEventDetection(
            pageManagerConfig, 
            this.config.autoDetectionEnabled, 
            this.config.montage, 
            this.config.montageMapping, 
            this.onSDEventPageLoaded, 
            this.onSDEventPageUnloaded,
            this.onSDEventPageLoadStart
        )
    }

    protected onSDEventPageLoaded = (page: ModalityPage) => {
		page.renderCache.forEach(dataObject => dataObject.forEach(cachedRender => (cachedRender.dirty = true)))
		const pagesInView = this.timeSeriesPageManager.eventDetectionPageManager.getPagesInView()
		this.renderer?.timeline?.updateNotLoadedRegions()

		if (pagesInView.includes(page)) {
			this.renderer?.sdEventCanvas?.renderPage(page)
		}
	}

    protected onSDEventPageUnloaded = (page: ModalityPage) => {
        // Optionally implement this if we want to do something when SD pages unload.
	}

    protected onSDEventPageLoadStart = (page: ModalityPage) => {
        // Optionally implement this if we want to do something when SD pages start loading.
    }
}
